import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/dialog";
import { Card } from "../../../../components/card";
import { useCampaignPrograms } from "../../hooks";
import { ProgramEditor, ProgramFormValues } from "./ProgramEditor";
import { ProgramTable } from "./ProgramTable";

interface ProgramSectionProps {
  campaignId: string;
  selectedProgramId: string;
  onSelectProgram: (programId: string) => void;
  onCreateProgram: (values: ProgramFormValues) => void | Promise<void>;
  onUpdateProgram: (
    programId: string,
    values: ProgramFormValues,
  ) => void | Promise<void>;
}

export function ProgramSection({
  campaignId,
  selectedProgramId,
  onSelectProgram,
  onCreateProgram,
  onUpdateProgram,
}: ProgramSectionProps) {
  const navigate = useNavigate();
  const { data: programs, isLoading, reload } =
    useCampaignPrograms(campaignId);
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreateProgram = async (values: ProgramFormValues) => {
    await onCreateProgram(values);
    await reload();
    onSelectProgram("");
  };

  const handleUpdateProgram = async (
    programId: string,
    values: ProgramFormValues,
  ) => {
    await onUpdateProgram(programId, values);
    await reload();
    onSelectProgram("");
  };

  const handlePaginationChange = () => {
    onSelectProgram("");
  };

  const handleOpenProgramDetail = (programId: string) => {
    navigate(`/campaign/${campaignId}/program/edit`, {
      state: {
        selectedProgramId: programId,
      },
    });
  };

  return (
    <Card className="rounded-[20px] border-[#DDEBE3] p-0">
      <div className="border-b border-[#E6EFE9] px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[#004232]">Programs</h2>
            <p className="mt-1 text-sm text-[#6A7282]">
              Manage programs inline. Select one row to load and update its details.
            </p>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="primary" type="button" size="sm">
                Create
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Create Program</DialogTitle>
                <DialogDescription>
                  Fill in the program details and save to add it to this campaign.
                </DialogDescription>
              </DialogHeader>
              <ProgramEditor
                selectedProgram={null}
                onSubmit={async (values) => {
                  await handleCreateProgram(values);
                  setCreateOpen(false);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="px-6 py-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#004232]">
            Table Section
          </h3>
          <p className="mt-1 text-sm text-[#6A7282]">
            Browse current programs in this campaign and pick one to edit.
          </p>
        </div>

        <ProgramTable
          programs={programs}
          isLoading={isLoading}
          selectedProgramId={selectedProgramId}
          onSelectProgram={onSelectProgram}
          onUpdateProgram={handleUpdateProgram}
          onOpenProgramDetail={handleOpenProgramDetail}
          onPaginationChange={handlePaginationChange}
        />
      </div>
    </Card>
  );
}
